// React.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';

// Third-party packages.
import * as dayjs from 'dayjs';

import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

import { ToastProvider } from 'react-toast-notifications';

// Context.
import { InstanceContext } from './context/InstanceContext';
import { HostContext } from './context/HostContext';
import { EventTypeContext } from './context/EventTypeContext';
import { AgentContext } from './context/AgentContext';
import { InstanaIntegrationContext } from './context/InstanaIntegrationContext';
import { LicenceContext } from './context/LicenceContext';
import { FilterContext } from './context/FilterContext';
import { TimeRangeContext, TimeRangeContextType, SetTimeRangeParamType, parseTimeRangeFromSearchParams, getConvertedTimeRangeRedirectSearch } from "./context/TimeRangeContext";
import { computeInitialTimeRange } from "./hooks/useInitialTimeRange";
import { ChatContext } from './context/ChatContext';
import ThemeContext from './context/ThemeContext';
import useThemePreference from './hooks/useThemePreference';
import usePrimeReactTheme from './hooks/usePrimeReactTheme';
import Highcharts from 'highcharts';
import { getHighchartsThemeOptions } from './helpers/chartTheme';

// Helpers.
import Helper from './helpers/Helper';
import { fetchFilters } from './helpers/utils';
import { normalizeTz } from './helpers/timeRangeUtils';
import { setBootstrapActive } from './utils/showAlert';
import ConditionalRender from "./helpers/ConditionalRender";

// Types.
import InstanceTarget from './types/instance/InstanceTarget';
import HostTarget from './types/host/HostTarget';
import EventType from './types/instance/EventType';
import Agent from './types/instance/Agent';
import InstanaIntegration from './types/integration/InstanaIntegration';
import Licence from './types/Licence';
import Filter from './types/Filter';
import FilterOption from './types/instance/FilterOption';
import { TimeRange } from "./types/TimeRange";

// Constants.
import {
    DATA_LOADED,
    DATA_LOADING, initialMessage, messages,
} from './component/Constants';
import { TARGET_COLOUR_CODES } from './component/Constants';

// Apis.
import InstanceApi from './api/instance/InstanceApi';
import HostApi from './api/host/HostApi';
import EventTypeApi from './api/instance/EventTypeApi';
import AgentApi from './api/instance/AgentApi';
import IntegrationsApi from './api/IntegrationApi';
import LicenceApi from './api/LicenceApi';

// Components.
import MenuSide from './component/MenuSide';
import Footer from './component/Footer';
import ChatComponent from "./component/chatGPT/index";
import AlertDisplay from './component/AlertDisplay';
import Prompts from './component/chatGPT/Prompts';

// General Views.
//import Dashboard from './views/Dashboard';
import EventHistory from './views/events/EventHistory';
import SQLSearch from './views/sql-search';
import Recommendations from './views/recommendations';
import Dashboard from './views/dashboard';

import DBVersionsList from './views/db-versions/List';
// Views.
import Instances from './views/instances/Instances';
import Hosts from './views/hosts/Hosts';
import Admin from './views/admin/Admin';
import NotFound from './views/NotFound';
import Reports from './views/reports/Reports';
import UserSettings from "./views/user-settings/UserSettings";
import LoginsDataTable from "./types/LoginsDataTable";
import LoginApi from "./api/LoginApi";


const options = Helper.getFilterParams()

function Content(props: { loginRequired: boolean }) {
    // Helper function to determine initial menu state based on screen size
    const getInitialMenuState = () => {
        if (typeof window !== 'undefined') {
            return window.innerWidth >= 1200 ? 'expanded' : 'collapsed';
        }
        return 'collapsed'; // Default to collapsed for SSR
    };

    const themeCtx = useThemePreference();
    usePrimeReactTheme(themeCtx.theme);

    // Apply Highcharts global theme when resolved theme changes
    useEffect(() => {
        Highcharts.setOptions(getHighchartsThemeOptions(themeCtx.theme));
    }, [themeCtx.theme]);

    const [updateKey, setUpdateKey] = useState<number>(0);
    const [loading, setLoading] = useState<number>(DATA_LOADING);
    const [className, setClassName] = useState<string>(getInitialMenuState());
    const [instances, setInstances] = useState<InstanceTarget[]>([]);
    const [hosts, setHosts] = useState<HostTarget[]>([]);
    const [eventTypes, setEventTypes] = useState<EventType[]>([]);
    const [agents, setAgents] = useState<Agent[]>([]);
    const [instanaIntegrations, setInstanaIntegrations] = useState<InstanaIntegration[]>([]);
    const [licences, setLicences] = useState<Licence[]>([]);
    const [licencesLoaded, setLicencesLoaded] = useState<number>(DATA_LOADING);
    const [filters, setFilters] = useState<Filter[]>([]);
    const [currentFilterOptions, setCurrentFilterOptions] = useState<FilterOption[]>(options);
    const [message, setMessage] = useState<string>(initialMessage[Math.floor(random() * initialMessage.length)]);

    // --- TimeRangeContext state management ---
    // Compute initial time range from URL + localStorage (runs once via useState initialiser)
    const [timeRangeState, setTimeRangeState] = useState<TimeRange>(() => computeInitialTimeRange(window.location.search));

    const setTimeRange = useCallback((newTimeRange: SetTimeRangeParamType) => {
        // Add type annotation for prevTimeRange
        setTimeRangeState((prevTimeRange: TimeRange) => {
            const from = newTimeRange.from;
            const to = newTimeRange.to;
            const updated = {
                ...prevTimeRange, // Keep existing properties like dateFormat
                previousFrom: from.subtract(newTimeRange.to.diff(from, 'millisecond'), 'millisecond'),
                from: from,
                to: to,
                timezone: normalizeTz(newTimeRange.timezone),
                interval: newTimeRange.interval,
                period: newTimeRange.period,
                autorefresh: newTimeRange.autorefresh ?? false,
            };
            // Optionally save to localStorage here if needed
            // window.localStorage.setItem('dbmar-timeZone', JSON.stringify(updated.timezone));
            // window.localStorage.setItem('dbmar-interval', JSON.stringify(updated.interval));
            return updated;
        });
    }, []);

    const applyPeriod = useCallback(function (periodOption: number, from: any, to: any, timeZone?: string, autorefresh?: boolean) {
        const agg = JSON.parse(window.localStorage.getItem('dbmar-interval') || '0')
        // Use the state-managed timeRange for validation
        const aggValid = Helper.aggregateIsValid(agg, { timeRange: timeRangeState })

        // Use the state-setting function
        setTimeRange({
            from: from,
            to: to,
            timezone: normalizeTz(timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone),
            interval: aggValid ? agg : 0,
            period: periodOption,
            autorefresh: autorefresh || false
        });
    }, [filters, timeRangeState]);

    const timeRangeContextValue: TimeRangeContextType = useMemo(() => ({
        timeRange: timeRangeState,
        setTimeRange: setTimeRange,
        applyPeriod: applyPeriod,
    }), [timeRangeState, setTimeRange, applyPeriod]);
    // --- End TimeRangeContext state management ---

    const location = useLocation();
    const navigate = useNavigate();
    // If link has fm/to/tz in a different timezone than user's, convert and redirect to user's timezone.
    // Otherwise sync context from URL for same-tab navigation.
    useEffect(() => {
        const convertedSearch = getConvertedTimeRangeRedirectSearch(location.search);
        if (convertedSearch) {
            navigate({ pathname: location.pathname, search: convertedSearch }, { replace: true });
            return;
        }
        const parsed = parseTimeRangeFromSearchParams(location.search);
        if (parsed) {
            setTimeRange(parsed);
        }
    }, [location.search, location.pathname, navigate, setTimeRange]);

    const [isOpen, setIsOpen] = useState(false);
    const [chatData, setChatData] = useState('');
    const loginRequired = props.loginRequired
    const [isAdmin, setIsAdmin] = useState<boolean>(false);

    const getLogins = useMemo(() => {
        return async () => {
            if (loginRequired) {
                const savedToken = window.localStorage.getItem("dbmar-token");
                let loginsData: LoginsDataTable[] = await LoginApi.getLogins();
                if (savedToken) {
                    const decodedToken = atob(savedToken).split(":");
                    // For SSO tokens (format: "sso:username:timestamp"), username is at index 1
                    // For regular tokens (format: "username:password"), username is at index 0
                    const isSSOToken = savedToken.startsWith('c3Nv'); // Base64 encoded "sso:"
                    const username = isSSOToken ? decodedToken[1] : decodedToken[0];
                    const findUser: any = loginsData.find(user => user.name === username);
                    setIsAdmin(findUser ? findUser?.admin : false);
                }
            } else {
                setIsAdmin(true);
            }
        };
    }, [loginRequired]);

    useEffect(() => {
        void getLogins();
    }, [loginRequired]);

    // Handle responsive menu state on window resize
    useEffect(() => {
        const handleResize = () => {
            const newState = window.innerWidth >= 1200 ? 'expanded' : 'collapsed';
            setClassName(newState);
        };

        window.addEventListener('resize', handleResize);
        
        // Cleanup event listener on component unmount
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const toggleChat = (value: any) => {
        setChatData(value);
        setIsOpen(value ? true : !isOpen);
    };

    const toggleMenu = useCallback(function () {

        // Set the target class name to toggle the menu and content area being collapsed or expanded.
        if (className === 'expanded') {
            setClassName('collapsed');
        } else {
            setClassName('expanded');
        }
    }, [className]);

    const setFilterOptions = useCallback(function (options: FilterOption[]) {

        setCurrentFilterOptions(options);

        setUpdateKey(dayjs.default().valueOf());
    }, []);

    useEffect(() => {
        const getData = async () => {
            setBootstrapActive(true);
            try {
            // Fetch instances with tags in bulk
            const instancesWithTags = await InstanceApi.getInstancesWithTags();

            setInstances(instancesWithTags); // Set state with instances including tags

            const hosts = await HostApi.getHosts();
            for (let index = 0; index < hosts.length; index++) {

                if (index >= TARGET_COLOUR_CODES.length) {
                    break;
                }

                // Set a unique colour for the host.
                hosts[index].color = TARGET_COLOUR_CODES[index]
            }

            setHosts(hosts);

            let eventTypes: EventType[] = await EventTypeApi.getEventTypes();

            setEventTypes(eventTypes);

            let agents: Agent[] = await AgentApi.getAgents();

            setAgents(agents);

            IntegrationsApi.getIntegrations().then((instanaIntegrations: InstanaIntegration[]) => {
                setInstanaIntegrations(instanaIntegrations);
            })
            LicenceApi.getLicences().then((licences: Licence[]) => {
                for (let index = 0; index < licences.length; index++) {

                    // Build the licence settings (via the random JSON properties field).
                    licences[index].settings = JSON.parse(licences[index].properties);

                    if (licences[index].settings && licences[index].settings?.enddate) {

                        // Get the end date.
                        const enddate = licences[index].settings?.enddate;

                        if (enddate !== undefined) {

                            // Depending upon the licence expiry, set the CSS class name to be applied.
                            if (dayjs.default().isAfter(dayjs.default(enddate))) {

                                // Licence has expired.
                                licences[index].class = 'text-danger';

                            } else if (dayjs.default().add(1, 'month').isAfter(enddate)) {

                                // Due to expire in the next four weeks.
                                licences[index].class = 'text-amber';

                            } else if (dayjs.default().add(6, 'week').isAfter(enddate)) {

                                // Due to expire in the next six weeks.
                                licences[index].class = 'text-warning';
                            }
                        }
                    }
                }

                setLicences(licences);
                setLicencesLoaded(DATA_LOADED);
            });
            fetchFilters().then(setFilters)
            setLoading(DATA_LOADED);
            } finally {
                setBootstrapActive(false);
            }
        };
        getData();

    }, [timeRangeState]);

    function random(): number {
        return process.env.NODE_ENV === 'test' ? 0 : Math.random();
    }


    useEffect(() => {

        let timeout: NodeJS.Timeout;

        if (loading !== DATA_LOADED) {
            timeout = setInterval(() => setMessage(messages[Math.floor(random() * messages.length)]), 9000);
        }

        return () => {
            clearTimeout(timeout);
        };
    }, [loading, message]);


    if (document.location.pathname === '/' && process.env.NODE_ENV !== 'test') {

        // Todo: remove this JavaScript redirect once we have a root dashboard view sorted.
        document.location.pathname = `${document.location.pathname}dashboard`
    }

    return (
        <>
            <ConditionalRender if={loading === DATA_LOADED}>
                <div>
                    <div className={`right-panel ${isOpen ? 'chat-visible' : 'chat-hidden'}`}>
                        <div className="chat-arrow" onClick={() => toggleChat('')}>
                            <i className={`fal  fa-fw  ${!isOpen ? 'fa-angle-left' : 'fa-angle-right'}`}></i>
                        </div>
                        <ChatComponent data={chatData}/>
                    </div>
                    <div className={`container-fluid ${className}`}>
                        <AlertDisplay />
                        <MenuSide mustLogIn={loginRequired}
                                  isAdmin={isAdmin}
                                  instances={instances}
                                  hosts={hosts}
                                  licences={licences}
                                  toggleMenu={toggleMenu}
                                  isCollapsed={className === 'collapsed'}
                                  loading={licencesLoaded}/>
                        <ThemeContext.Provider value={themeCtx}>
                        <InstanceContext.Provider value={{ instances, setInstances }}>
                            <HostContext.Provider value={{ hosts, setHosts }}>
                                <EventTypeContext.Provider value={{ eventTypes, setEventTypes }}>
                                    <AgentContext.Provider value={{ agents, setAgents }}>
                                        <InstanaIntegrationContext.Provider
                                            value={{ instanaIntegrations, setInstanaIntegrations }}>
                                        <LicenceContext.Provider value={{ licences, setLicences }}>
                                            <FilterContext.Provider value={{ filters, setFilters, currentFilterOptions, setCurrentFilterOptions }}>
                                                <TimeRangeContext.Provider value={timeRangeContextValue}> {/* Add Provider */}
                                                    <ChatContext.Provider
                                                        value={{ isOpen, chatData, toggleChat }}>
                                                        <ToastProvider>
                                                            <Routes>
                                                                {/* Event History */}
                                                                <Route
                                                                path="/event-history"
                                                                element={
                                                                    <EventHistory
                                                                        key={updateKey}
                                                                        instances={instances}
                                                                        hosts={hosts}
                                                                        eventTypes={eventTypes}
                                                                        toggleMenu={toggleMenu}

                                                                    />
                                                                }
                                                            />

                                                            {/* SQL Search */}
                                                            <Route
                                                                path="/sql-search"
                                                                element={
                                                                    <SQLSearch
                                                                        key={updateKey}
                                                                        instances={instances}
                                                                    />
                                                                }
                                                            />

                                                            {/* Recommendations */}
                                                            <Route
                                                                path="/recommendations/*"
                                                                element={
                                                                    <Recommendations />
                                                                }
                                                            />

                                                            {/* Dashboard */}
                                                            <Route
                                                                path="/dashboard"
                                                                element={
                                                                    <Dashboard
                                                                        key={updateKey}
                                                                        toggleMenu={toggleMenu}

                                                                    />
                                                                }
                                                            />

                                                            {/* Instances */}
                                                            <Route
                                                                path="/instances/*"
                                                                element={
                                                                    <Instances
                                                                        key={updateKey}
                                                                        instances={instances}
                                                                        hosts={hosts}
                                                                        eventTypes={eventTypes}
                                                                        toggleMenu={toggleMenu}

                                                                        setFilterOptions={setFilterOptions}
                                                                    />
                                                                }
                                                            />

                                                            {/* Hosts */}
                                                            <Route
                                                                path="/hosts/*"
                                                                element={
                                                                    <Hosts
                                                                        key={updateKey}
                                                                        instances={instances}
                                                                        hosts={hosts}
                                                                        eventTypes={eventTypes}
                                                                        toggleMenu={toggleMenu}

                                                                        setFilterOptions={setFilterOptions}
                                                                    />
                                                                }
                                                            />

                                                            {/* Reports */}
                                                            <Route
                                                                path="/reports/*"
                                                                element={
                                                                    <Reports
                                                                        key={updateKey}
                                                                        instances={instances}
                                                                        hosts={hosts}
                                                                        eventTypes={eventTypes}

                                                                        toggleMenu={toggleMenu}
                                                                    />
                                                                }
                                                            />
                                                            {/* DB Versions */}
                                                            <Route
                                                                path="/db-versions"
                                                                element={<DBVersionsList />}
                                                            />

                                                            {/* Admin */}
                                                            <Route
                                                                path="/admin/*"
                                                                element={
                                                                    <Admin
                                                                        key={updateKey}
                                                                        isAdmin={isAdmin}
                                                                        instances={instances}
                                                                        loginRequired={loginRequired}
                                                                        hosts={hosts}
                                                                        eventTypes={eventTypes}
                                                                        agents={agents}
                                                                        instanaIntegrations={instanaIntegrations}
                                                                        licences={licences}
                                                                        toggleMenu={toggleMenu}

                                                                    />
                                                                }
                                                            />

                                                            {/* Not Found Page */}
                                                            <Route path="/not-found" element={<NotFound />} />

                                                            {/* Login Page */}
                                                            <Route
                                                                path="/login"
                                                                element={<Navigate to="/dashboard" replace />}
                                                            />

                                                            {/* User Settings */}
                                                            <Route
                                                                path="/user-settings/*"
                                                                element={
                                                                    <UserSettings
                                                                        toggleMenu={toggleMenu}
                                                                        applyPeriod={applyPeriod}
                                                                        loginRequired={loginRequired}
                                                                    />
                                                                }
                                                            />

                                                            {/* Redirect old change-password route */}
                                                            <Route
                                                                path="/change-password"
                                                                element={<Navigate to="/user-settings/change-password" replace />}
                                                            />

                                                            {/* Error Page */}
                                                            <Route path="/error" element={<Navigate to="/dashboard" replace />} />

                                                            {/* Catch-all route */}
                                                                <Route path="*" element={<Navigate to="/not-found" />} />
                                                            </Routes>
                                                        </ToastProvider>
                                                    </ChatContext.Provider>
                                                </TimeRangeContext.Provider> {/* Close Provider */}
                                            </FilterContext.Provider>
                                        </LicenceContext.Provider>
                                    </InstanaIntegrationContext.Provider>
                                    </AgentContext.Provider>
                                </EventTypeContext.Provider>
                            </HostContext.Provider>
                        </InstanceContext.Provider>
                        </ThemeContext.Provider>
                        <Footer/>
                        <Tooltip id="global-tooltip" className="dbmarlin-tooltip" />
                        <Tooltip
                            id="error-tooltip"
                            className="dbmarlin-tooltip"
                            clickable={true}
                            render={({ activeAnchor }) => {
                                const message = activeAnchor?.getAttribute('data-error-message') ?? '';
                                const dbType = activeAnchor?.getAttribute('data-db-type') ?? '';
                                const targetKind = (activeAnchor?.getAttribute('data-target-kind') as 'database' | 'host' | null) ?? 'database';
                                return (
                                    <div>
                                        <div>{message}</div>
                                        {dbType && (
                                            <div style={{ marginTop: '8px' }}>
                                                <button
                                                    type="button"
                                                    className="btn btn-xsm btn-primary"
                                                    onClick={() => toggleChat(Prompts.getConnectivityErrorPrompt(message, dbType, targetKind))}
                                                >
                                                    Ask AI Co-pilot
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            }}
                        />,
                    </div>
                </div>
            </ConditionalRender>

            <ConditionalRender if={loading === DATA_LOADING}>
                <div className="initialising">
                    <div className="w-100 text-center text-muted">
                        <div className="spinner"><img src="/icons/icon.png" className="fish" width="100"
                                                      height="100"
                                                      alt="DBmarlin"/></div>
                        <div className="background">
                            <div className="waves"></div>
                            <div className="message">{message}</div>
                        </div>
                    </div>
                </div>
            </ConditionalRender>
        </>
    );
}

export default Content;
